import { useMemo, useState } from "react";

import { Sparkline } from "@/components/cinch/Sparkline";
import { StatusBadge } from "@/components/cinch/StatusBadge";
import { FilterBar, MultiSelectChip } from "@/components/cinch/FilterBar";
import { Button } from "@/components/ui/button";
import { useCan } from "@/lib/auth";

import { useAckNewEntity, useNewEntities, type FirstSeenOut } from "../api";

const KIND_OPTIONS = [
  { value: "new_integration", label: "New integration" },
  { value: "new_company", label: "New company" },
  { value: "new_location", label: "New location" },
  { value: "went_quiet", label: "Went quiet" },
  { value: "step_change", label: "Step change" },
];

/** New & Changed — the improved "New Data": a first-seen LEDGER with real
 *  time periods (first event date AND first arrival date side by side — the
 *  two clocks again), grouped by week, one row per transition. Every row
 *  opens the entity drawer: "what's that about?" is one click (decision #9). */
export function NewChangedTab({
  onOpenEntity,
}: {
  onOpenEntity: (level: string, keyPath: string) => void;
}) {
  const canWrite = useCan("integrations.write");
  const [kinds, setKinds] = useState<string[]>([]);
  const [days, setDays] = useState(45);
  const rows = useNewEntities(days, kinds.length === 1 ? kinds[0] : undefined);
  const ack = useAckNewEntity();

  const groups = useMemo(() => {
    const filtered = (rows.data ?? []).filter(
      (r) => kinds.length === 0 || kinds.includes(r.kind),
    );
    const now = new Date();
    const bucket = (iso: string): string => {
      const d = new Date(iso);
      const age = (now.getTime() - d.getTime()) / 86_400_000;
      if (age <= 7) return "This week";
      if (age <= 14) return "Last week";
      return "Older";
    };
    const out = new Map<string, FirstSeenOut[]>();
    for (const r of filtered) {
      const key = bucket(r.detected_on);
      out.set(key, [...(out.get(key) ?? []), r]);
    }
    return ["This week", "Last week", "Older"]
      .filter((k) => out.has(k))
      .map((k) => [k, out.get(k) ?? []] as const);
  }, [rows.data, kinds]);

  return (
    <div className="flex flex-col gap-4">
      <FilterBar>
        <MultiSelectChip label="Kind" options={KIND_OPTIONS} value={kinds} onChange={setKinds} />
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          Window:
          {[45, 90, 180].map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDays(d)}
              className={`rounded px-2 py-1 ${days === d ? "bg-muted font-medium" : "hover:bg-muted"}`}
            >
              {d}d
            </button>
          ))}
        </div>
      </FilterBar>

      {groups.length === 0 && (
        <p className="py-10 text-center text-sm text-muted-foreground">
          {rows.isLoading ? "Loading…" : "Nothing new or changed in this window."}
        </p>
      )}

      {groups.map(([label, items]) => (
        <div key={label}>
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            {label}
          </h2>
          <div className="space-y-1.5">
            {items.map((r) => (
              <div
                key={r.id}
                className="flex cursor-pointer flex-wrap items-center gap-3 rounded-md border px-3 py-2 text-sm hover:bg-muted"
                onClick={() => onOpenEntity(r.level, r.key_path)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && onOpenEntity(r.level, r.key_path)}
              >
                <StatusBadge value={r.kind} />
                {r.detail.inactive && (
                  <StatusBadge value="inactive" label="Inactive in Cinch" />
                )}
                <div className="flex min-w-40 flex-col">
                  <span className="font-medium">
                    {r.location_name ?? r.company_name ?? r.entity_source ?? r.key_path}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {r.key_path}
                    {r.detail.entity_ref ? ` · ref ${r.detail.entity_ref}` : ""}
                    {r.detail.entity_number && r.detail.entity_number !== r.detail.entity_ref
                      ? ` · #${r.detail.entity_number}`
                      : ""}
                  </span>
                </div>
                <span className="text-xs text-muted-foreground">
                  first event {r.first_event_date ?? "—"} · first arrival{" "}
                  {r.first_arrival_date ?? "—"}
                </span>
                {r.detail.ramp && r.detail.ramp.length > 1 && (
                  <Sparkline points={r.detail.ramp} height={22} ariaLabel="30-day ramp" />
                )}
                {r.detail.prior_rate != null && (
                  <span className="text-xs text-muted-foreground">
                    {r.detail.prior_rate}/day → {r.detail.recent_rate}/day
                  </span>
                )}
                {r.annotation && (
                  <span className="text-xs text-muted-foreground">{r.annotation}</span>
                )}
                <div className="flex-1" />
                {r.acknowledged_at ? (
                  <span className="text-xs text-muted-foreground">acknowledged</span>
                ) : (
                  canWrite && (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={ack.isPending}
                      onClick={(e) => {
                        e.stopPropagation();
                        ack.mutate(r.id);
                      }}
                    >
                      Acknowledge
                    </Button>
                  )
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
